import {
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Edit } from '@mui/icons-material';
import CreateExecutiveCategory from './CreateExecutiveCategory';
import DeleteConfirmation from 'src/Utils/DeleteConfirmation';
import ModalsWrapper from 'src/components/Modals/ModalsWrapper';
import { IExecutiveCategory } from './types';
import EditExecutiveCategory from './EditExecutiveCategory';

function ExecutiveCategoryList() {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [exeutiveMembers, setExeutiveMembers] = useState<
    IExecutiveCategory[] | null
  >(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const token = localStorage.getItem(TOKEN_KEY);

  // ▶ Create Category Modal state
  const [createOpen, setCreateOpen] = useState(false);

  const handleCreateOpen = () => setCreateOpen(true);
  const handleCreateClose = () => setCreateOpen(false);

  // ▶ Delete Modal state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<IExecutiveCategory | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<IExecutiveCategory | null>(
    null
  );

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const uri = `${url}/api/v1/admin/executive/categories`;
        const { data } = await axios.get(uri, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (data.success) {
          setExeutiveMembers(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [refreshTrigger]);

  const handleOpenDelete = (category: IExecutiveCategory) => {
    setSelectedCategory(category);
    setDeleteOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedCategory(null);
  };

  const handleDelete = async () => {
    if (!selectedCategory?.id) return;
    try {
      setDeleteLoading(true);

      await axios.delete(
        `${url}/api/v1/admin/executive/${selectedCategory.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh list
      setRefreshTrigger((prev) => prev + 1);
      handleCloseDelete();
    } catch (error) {
      console.error('Failed to delete category', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Container maxWidth='xl'>
      <Card>
        <CardHeader
          action={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Button
                onClick={handleCreateOpen}
                variant='contained'
                sx={{ ml: 2 }}
              >
                Create Category
              </Button>
            </Box>
          }
          title='Executive Category List'
        />
        <Divider />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SL.</TableCell>
                <TableCell>Category Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            {loading ? (
              <TableLoader />
            ) : exeutiveMembers?.length ? (
              <TableBody>
                {exeutiveMembers.map(
                  (exeutiveMember: IExecutiveCategory, i: number) => (
                    <TableRow hover key={exeutiveMember.id || i}>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {exeutiveMember?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant='body1'
                          color='text.primary'
                          gutterBottom
                          noWrap
                        >
                          {exeutiveMember?.status ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {/* EDIT – ready when you implement UpdateExecutiveCategory */}
                        <Tooltip title='Edit' arrow>
                          <IconButton
                            sx={{
                              '&:hover': {
                                background: theme.colors.primary.lighter,
                              },
                              color: theme.palette.primary.main,
                            }}
                            color='inherit'
                            size='small'
                            onClick={() => {
                              setEditCategory(exeutiveMember);
                              setEditOpen(true);
                            }}
                          >
                            <Edit fontSize='small' />
                          </IconButton>
                        </Tooltip>

                        {/* DELETE */}
                        <Tooltip title='Delete' arrow>
                          <IconButton
                            onClick={() => handleOpenDelete(exeutiveMember)}
                            sx={{
                              '&:hover': {
                                background: theme.colors.error.lighter,
                              },
                              color: theme.palette.error.main,
                            }}
                            color='inherit'
                            size='small'
                          >
                            <DeleteTwoToneIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            ) : (
              <TableNotFound />
            )}
          </Table>
        </TableContainer>

        {/* CREATE CATEGORY MODAL */}
        <ModalsWrapper
          setShowModal={setCreateOpen}
          showModal={createOpen}
          modalData={{ title: 'Create Executive Category' }}
          maxWidth='sm'
        >
          <CreateExecutiveCategory
            handleClose={handleCreateClose}
            setRefreshTrigger={setRefreshTrigger}
          />
        </ModalsWrapper>

        {/* DELETE CONFIRMATION MODAL */}
        <DeleteConfirmation
          open={deleteOpen}
          handleClose={handleCloseDelete}
          text={
            selectedCategory
              ? `Want to delete "${selectedCategory.name}" category?`
              : 'Want to delete this category?'
          }
          handleDeleteFun={handleDelete}
          loading={deleteLoading}
        />

        {/* (Optional) EDIT MODAL – when you create UpdateExecutiveCategory */}

        <ModalsWrapper
          setShowModal={setEditOpen}
          showModal={editOpen}
          modalData={{ title: 'Edit Executive Category' }}
          maxWidth='sm'
        >
          {editCategory && (
            <EditExecutiveCategory
              handleClose={() => setEditOpen(false)}
              setRefreshTrigger={setRefreshTrigger}
              category={editCategory}
            />
          )}
        </ModalsWrapper>
      </Card>
    </Container>
  );
}

export default ExecutiveCategoryList;
